import React from 'react';
import {
  Page,
  Text,
  Document,
  Image,
  StyleSheet,
  View,
  Link,
  Svg,
  Path,
  Font,
} from '@react-pdf/renderer';
import { detectURLs, deleteURLs } from '../utils';
import PoppinsRegular from '../fonts/Poppins/poppins-400.ttf';
import PoppinsMedium from '../fonts/Poppins/poppins-500.ttf';
import PoppinsSemiBold from '../fonts/Poppins/poppins-600.ttf';

Font.register({
  family: 'Poppins',
  fonts: [
    {
      src: PoppinsRegular,
    },
    {
      src: PoppinsSemiBold,
      fontWeight: 'semibold',
    },
    {
      src: PoppinsMedium,
      fontWeight: 'medium',
    },
  ],
});

const basicStyles = StyleSheet.create({
  body: {
    paddingTop: 15,
    paddingBottom: 35,
    fontFamily: 'Poppins',
    backgroundColor: 'rgb(251,251,251)',
    color: 'rgb(85,85,85)',
  },
  main: {
    paddingHorizontal: 20,
    marginBottom: 10,
    paddingTop: 5,
    paddingBottom: 10,
  },
  pageNumber: {
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: 'black',
    position: 'absolute',
    fontSize: 12,
    bottom: 10,
    left: 20,
    right: 20,
    textAlign: 'center',
    color: 'rgb(85,85,85)',
  },
});

const headerStyles = StyleSheet.create({
  logo: {
    width: '156',
    height: '44',
    marginHorizontal: 'auto',
  },
  header: {
    paddingHorizontal: 35,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgb(227,227,227)',
  },
  headerTitle: {
    fontSize: 13,
    color: 'rgb(85,85,85)',
    fontWeight: 'semibold',
  },
  headerSubtitle: {
    fontSize: 13,
    color: 'rgb(85,85,85)',
    fontWeight: 'normal',
    marginBottom: 8,
  },
});

const heroStyles = StyleSheet.create({
  hero: {
    width: '100%',
    minHeight: 156,
    overflow: 'hidden',
    borderRadius: 5,
    marginBottom: 12,
    padding: 20,
    paddingBottom: 0,
    color: 'white',
    textAlign: 'center',
    position: 'relative',
  },
  heroBg: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    objectFit: 'cover',
    objectPosition: 'center',
  },
  heroTitle: {
    fontSize: 26,
    fontWeight: 'semibold',
    marginBottom: 5,
  },
  heroInfoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  heroInfoItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 'light',
    marginHorizontal: 12,
    marginBottom: 10,
  },
  heroInfoItemText: {
    marginLeft: 8,
    color: 'rgb(185,185,185)',
  },
  heroButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  heroButtonWrapper: {
    marginHorizontal: 12,
    marginBottom: 20,
  },
});

const stepStyles = StyleSheet.create({
  step: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    marginTop: 10,
    marginBottom: 0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgb(227,227,227)',
    borderRadius: 8,
    backgroundColor: 'white',
  },
  stepImage: {
    marginTop: 16,
    width: '100%',
    height: 'auto',
  },
  stepHead: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: '20px',
  },
  stepNumber: {
    color: 'rgb(106,40,234)',
    fontSize: 18,
    fontWeight: 'semibold',
    marginRight: 20,
  },
  stepTitle: {
    fontSize: 16,
    color: 'rgb(85,85,85)',
    marginRight: '5px',
  },
  stepTitleInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepDesc: {
    fontSize: 16,
    color: 'rgb(85,85,85)',
  },
});

const buttonStyles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: 'rgb(229, 216, 255)',
    borderRadius: 26,
    paddingHorizontal: 8,
    paddingVertical: 6,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  buttonLink: {
    fontSize: 12,
    textDecoration: 'none',
    color: 'rgb(106,40,234)',
    marginLeft: 4,
  },
});

const getUrl = (url) => {
  const { hostname } = new URL(url);
  return hostname;
};

const PDFFile = ({ data }) => {
  const heroBg = data.customHeroImg ? data.customHeroImg : data.heroImg;

  return (
    <Document>
      <Page style={basicStyles.body} size='A4'>
        <View style={headerStyles.header} fixed>
          <Svg
            style={headerStyles.logo}
            width='156'
            height='44'
            viewBox='0 0 156 44'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <Path
              d='M24.8001 32.152C28.2354 32.152 31.0202 29.3672 31.0202 25.932C31.0202 22.4967 28.2354 19.7119 24.8001 19.7119C21.3649 19.7119 18.5801 22.4967 18.5801 25.932C18.5801 29.3672 21.3649 32.152 24.8001 32.152Z'
              fill='#6A28EA'
            />
            <Path
              d='M91.4401 10.4184C91.4401 11.7274 90.8059 12.9958 89.9928 12.9958C89.5781 12.9958 89.3017 12.6056 89.3586 12.1258C89.4155 11.7518 89.7164 10.6379 90.0741 9.36137C91.4726 4.31215 92.5703 0.628906 91.1149 0.628906C89.8546 0.628906 89.1472 5.40981 88.6187 9.54025C88.5293 10.2395 88.3667 11.5486 88.2772 12.0039C88.1959 12.4267 87.9195 13.0934 87.2527 13.0934C86.7161 13.0934 86.4071 12.6218 86.5535 11.8087C86.6998 10.955 87.1958 8.38568 87.2039 8.32876C87.269 7.98727 87.0088 7.67017 86.6592 7.67017C86.1795 7.67017 86.0982 8.26371 85.5046 9.67847C84.5696 11.9063 84.0736 12.76 83.5939 12.76C83.0329 12.76 82.8946 11.9714 82.8946 11.1745C82.8946 8.88979 84.098 6.89774 85.0737 6.89774C85.5128 6.89774 85.7892 7.13353 85.7892 7.3612C86.334 7.00344 86.6267 5.58055 85.0737 5.58055C83.1548 5.58055 81.7563 7.94661 81.7563 11.3372C81.7563 11.4916 81.7645 11.6461 81.7726 11.7925C81.6181 12.321 81.2929 13.004 80.6505 13.004C80.1627 13.004 79.8212 12.5568 79.9432 11.9063C80.431 9.24754 80.9676 6.45055 80.9758 6.44242C81.0327 6.14158 80.8294 5.84887 80.5286 5.80009C80.2277 5.74317 79.935 5.94644 79.8862 6.24728C79.7074 7.20671 79.1707 8.91418 78.4878 10.3208C77.6259 12.1014 76.9104 12.9633 76.39 12.9633C75.9184 12.9633 75.894 12.4999 75.9997 12.0608C76.4469 10.1257 77.4795 7.88157 77.4795 6.78391C77.4795 6.10093 77.073 5.52364 76.3494 5.52364C75.9184 5.52364 75.5363 5.88952 75.5363 6.44242C75.8615 6.44242 76.1461 6.69447 76.1461 7.08475C76.1461 7.784 75.4062 9.75978 74.9834 11.5892C74.9346 11.7925 74.9183 11.9876 74.9102 12.1746C74.7069 12.6625 74.3898 13.0365 73.8695 13.0365C73.3735 13.0365 73.1052 12.695 73.1052 12.2885C73.1052 11.4916 73.9508 9.23128 73.9508 7.82465C73.9508 6.60503 73.3247 5.79196 72.2433 5.79196C71.6091 5.79196 70.8936 6.43429 70.1293 7.70269L70.2919 6.71886C70.3976 6.10906 69.861 5.60495 69.0642 5.60495C69.2105 6.10093 69.178 6.62943 68.9829 7.70269C68.7633 8.92231 68.4788 10.4834 68.2674 11.6299C67.8446 12.7356 67.2673 12.9958 66.877 12.9958C66.4135 12.9958 66.0151 12.6381 66.1371 11.9876C66.3973 10.6054 67.3404 8.01166 67.3404 6.69447C67.3404 6.19036 67.1778 5.79196 66.6737 5.79196C66.0558 5.79196 65.8769 6.49933 65.7062 7.41811C65.4541 8.79222 65.2508 10.9631 65.0557 11.9957C64.9825 12.3616 64.576 13.0853 63.8767 13.0853C63.3401 13.0853 63.0311 12.6137 63.1775 11.8006C63.3238 10.9469 63.8198 8.37755 63.8279 8.32063C63.893 7.97914 63.6328 7.66204 63.2832 7.66204C62.8035 7.66204 62.7222 8.25558 62.1286 9.67034C61.1936 11.8982 60.6976 12.7519 60.2179 12.7519C59.6568 12.7519 59.5186 11.9632 59.5186 11.1664C59.5186 8.88165 60.722 6.88961 61.6977 6.88961C62.1367 6.88961 62.4132 7.12541 62.4132 7.35307C62.9579 6.99531 63.2507 5.57242 61.6977 5.57242C59.7788 5.57242 58.3803 7.93848 58.3803 11.329C58.3803 11.5079 58.3884 11.6786 58.4047 11.8413C58.1689 12.508 57.7705 13.0202 57.2095 13.0202C56.7135 13.0202 56.3557 12.6462 56.5184 11.7437C56.868 9.78417 57.4371 7.3612 57.6729 6.11719C57.7705 5.59682 57.1038 5.21467 56.7542 5.61308C56.2094 6.22289 55.5915 6.6213 55.0467 6.84896C55.0711 6.18223 54.9735 5.57243 54.6808 5.15775C54.4288 4.79187 54.0466 4.5886 53.6075 4.5886C52.7457 4.5886 51.9163 5.27159 51.9163 6.45868C51.9163 7.54008 52.6075 8.13362 53.5669 8.13362C53.697 8.13362 53.8352 8.12549 53.9816 8.1011C53.3555 10.1419 51.7375 12.7356 50.3146 12.7356C50.3146 13.2398 50.5585 13.8658 51.1846 13.8658C52.1928 13.8658 53.3067 12.3942 54.5263 9.23128C54.6971 8.79221 54.8353 8.30437 54.9329 7.82465C55.4126 7.62951 55.876 7.3612 56.2582 7.06036C56.2582 7.06036 55.6321 10.4346 55.4126 11.6136C55.193 12.7926 56.0712 13.8496 57.2583 13.8496C57.8356 13.8496 58.3722 13.5975 58.8031 13.1422C58.8438 13.2072 58.8844 13.2641 58.9332 13.3211C59.2178 13.6626 59.6081 13.8496 60.0553 13.8496C60.7952 13.8496 61.4212 13.0527 62.0961 11.7843C62.0392 12.2885 62.1937 12.7926 62.5108 13.1828C62.8522 13.5975 63.3645 13.8577 63.9743 13.8577C64.5109 13.8577 65.1207 13.6219 65.5679 13.2235C65.9257 13.63 66.4379 13.8577 66.9827 13.8577C67.186 13.8577 67.6006 13.817 68.0478 13.5487C68.1373 13.7276 68.3243 13.8577 68.5438 13.8577C68.804 13.8577 69.0398 13.6707 69.0886 13.4024C69.3244 12.1502 69.8041 10.1663 70.4708 8.75969C71.1457 7.32054 71.8124 6.62943 72.1945 6.62943C72.5116 6.62943 72.7068 7.03597 72.7068 7.74334C72.7068 8.75969 71.9018 10.8005 71.9018 12.0527C71.9018 13.3129 72.7881 13.8496 73.8695 13.8496C74.398 13.8496 74.9265 13.6544 75.3086 13.3373C75.5119 13.5812 75.8696 13.8496 76.4794 13.8496C77.2925 13.8496 78.0975 13.2154 78.8862 11.9551C78.8699 13.004 79.6992 13.8496 80.7562 13.8496C81.3254 13.8496 81.862 13.5975 82.236 13.1828C82.2686 13.2316 82.3011 13.2723 82.3336 13.3211C82.6182 13.6626 83.0085 13.8496 83.4557 13.8496C84.1956 13.8496 84.8216 13.0527 85.4965 11.7843C85.4396 12.2885 85.5941 12.7926 85.9112 13.1828C86.2527 13.5975 86.7649 13.8577 87.3747 13.8577C87.9195 13.8577 88.4886 13.6382 88.8545 13.2641C89.2041 13.6382 89.7001 13.8577 90.2286 13.8577C91.4157 13.8577 92.5378 12.5324 92.5378 11.516C92.489 11.0201 91.9523 10.4184 91.4401 10.4184ZM53.949 7.1254C53.4124 7.1254 53.0303 6.88961 53.0303 6.40176C53.0303 5.94644 53.3311 5.71065 53.575 5.71065C53.9653 5.71065 54.2092 6.06027 54.2092 6.64569C54.2092 6.79204 54.193 6.94653 54.1767 7.11727C54.0954 7.11727 54.0222 7.1254 53.949 7.1254Z'
              fill='#6A28EA'
            />
            <Path
              d='M51.0546 2.66144C52.6076 3.01107 53.9573 3.43387 55.0549 3.43387C56.1282 3.43387 57.4535 2.79967 57.4535 1.9622C55.4371 2.36874 53.3881 1.97846 51.3067 1.66949C51.5018 1.07594 51.7539 0.718187 52.136 0.718187C52.136 0.555571 51.9083 0.0839844 51.4449 0.0839844C50.8757 0.0839844 50.5424 0.44174 50.2253 1.52313C49.526 1.44183 48.8268 1.38491 48.1113 1.38491C43.9971 1.38491 41.9481 3.01107 41.9481 5.27143C41.9481 7.05207 43.3141 7.70253 44.1353 7.70253C45.111 7.70253 45.6232 6.83254 45.3224 6.26338C44.5581 6.65366 43.493 6.24712 43.493 5.06003C43.493 3.40948 45.2899 2.23864 47.5909 2.23864C48.4284 2.23864 49.2252 2.32808 49.9895 2.46631C49.8838 2.93789 49.7781 3.49891 49.6561 4.14938C49.217 6.57235 48.9081 8.2229 48.7129 9.29617C48.3145 11.4671 47.4608 12.6542 45.9973 12.6542C44.8833 12.6542 44.2166 11.8492 44.2166 11.4102C43.9808 11.4102 43.7613 11.7354 43.7613 12.2639C43.7613 13.3453 44.5906 14.199 45.8509 14.199C47.7047 14.199 49.152 13.1095 49.8594 9.25551C50.0708 8.12533 50.5098 5.65357 50.8595 3.71032C50.9245 3.32817 50.9814 2.98668 51.0546 2.66144Z'
              fill='#6A28EA'
            />
            <Path
              d='M67.064 4.84023C67.5519 4.84023 67.9422 4.44996 67.9422 3.96211C67.9422 3.47426 67.5519 3.08398 67.064 3.08398C66.5762 3.08398 66.1859 3.47426 66.1859 3.96211C66.1859 4.44996 66.5762 4.84023 67.064 4.84023Z'
              fill='#6A28EA'
            />
            <Path
              d='M42.6149 21.9639C43.2572 20.7606 44.1516 19.8337 45.2899 19.1832C46.4282 18.5328 47.7373 18.2075 49.2089 18.2075C51.0709 18.2075 52.6157 18.6547 53.8435 19.5328C55.0631 20.4191 55.8924 21.6794 56.3234 23.3218H52.884C52.6076 22.5575 52.1604 21.9639 51.5425 21.533C50.9245 21.1021 50.1521 20.8907 49.2171 20.8907C47.908 20.8907 46.8673 21.3541 46.0948 22.2729C45.3224 23.1998 44.9321 24.4845 44.9321 26.1513C44.9321 27.81 45.3224 29.1109 46.0948 30.0378C46.8673 30.9729 47.908 31.4363 49.2171 31.4363C51.0627 31.4363 52.2824 30.6232 52.884 29.0052H56.3234C55.8762 30.5744 55.0387 31.8185 53.8028 32.7372C52.5751 33.6641 51.0384 34.1195 49.2171 34.1195C47.7454 34.1195 46.4363 33.7861 45.298 33.1275C44.1597 32.4689 43.2653 31.5339 42.623 30.3305C41.9807 29.1272 41.6554 27.7368 41.6554 26.1432C41.6473 24.5658 41.9725 23.1673 42.6149 21.9639Z'
              fill='#020044'
            />
            <Path
              d='M58.982 21.9802C59.6243 20.785 60.5024 19.8581 61.6163 19.1995C62.7221 18.5409 63.9499 18.2075 65.2996 18.2075C66.5111 18.2075 67.5681 18.4433 68.4787 18.923C69.3812 19.4027 70.1049 19.9882 70.6496 20.7037V18.4677H73.8694V33.8837H70.6496V31.5908C70.1049 32.3144 69.3731 32.9242 68.4381 33.4121C67.503 33.8999 66.4379 34.1357 65.2508 34.1357C63.9255 34.1357 62.714 33.7942 61.6163 33.1112C60.5187 32.4283 59.6406 31.477 58.9982 30.2573C58.3559 29.0377 58.0307 27.6474 58.0307 26.1025C58.0225 24.5495 58.3396 23.1754 58.982 21.9802ZM69.9829 23.3787C69.5439 22.5981 68.9747 21.9965 68.2592 21.5899C67.5518 21.1834 66.7875 20.972 65.9663 20.972C65.1451 20.972 64.3808 21.1753 63.6734 21.5737C62.9661 21.9721 62.3888 22.5656 61.9497 23.338C61.5106 24.1105 61.2911 25.0292 61.2911 26.0944C61.2911 27.1595 61.5106 28.0946 61.9497 28.8914C62.3888 29.6963 62.9661 30.3061 63.6816 30.7208C64.3971 31.1436 65.1614 31.3469 65.9582 31.3469C66.7794 31.3469 67.5437 31.1436 68.2511 30.7289C68.9584 30.3224 69.5357 29.7207 69.9748 28.9239C70.4139 28.1271 70.6334 27.2002 70.6334 26.1432C70.6415 25.0862 70.422 24.1593 69.9829 23.3787Z'
              fill='#020044'
            />
            <Path
              d='M82.7564 18.9312C83.6914 18.4433 84.7403 18.2075 85.9192 18.2075C87.2608 18.2075 88.4886 18.5409 89.6025 19.1995C90.7083 19.8581 91.5864 20.7931 92.2206 21.9802C92.8548 23.1754 93.1719 24.5495 93.1719 26.0944C93.1719 27.6392 92.8548 29.0296 92.2206 30.2492C91.5864 31.4688 90.7164 32.4201 89.6025 33.1031C88.4886 33.7861 87.269 34.1276 85.9192 34.1276C84.7403 34.1276 83.6995 33.8918 82.7889 33.4121C81.8782 32.9324 81.1302 32.347 80.5529 31.6314V41.2014H77.3657V18.4514H80.5529V20.7199C81.0896 20.0126 81.8213 19.419 82.7564 18.9312ZM89.261 23.338C88.8219 22.5656 88.2446 21.9802 87.5291 21.5737C86.8136 21.1753 86.0412 20.972 85.22 20.972C84.4151 20.972 83.6589 21.1753 82.9434 21.5899C82.2279 21.9965 81.6425 22.5981 81.2115 23.3787C80.7725 24.1593 80.5529 25.0862 80.5529 26.1513C80.5529 27.2164 80.7725 28.1433 81.2115 28.932C81.6506 29.7288 82.2279 30.3305 82.9434 30.7371C83.6589 31.1436 84.4232 31.355 85.22 31.355C86.0412 31.355 86.8136 31.1436 87.5291 30.7289C88.2446 30.3061 88.8219 29.6963 89.261 28.8995C89.7001 28.0946 89.9196 27.1676 89.9196 26.1025C89.9196 25.0292 89.7001 24.1105 89.261 23.338Z'
              fill='#020044'
            />
            <Path
              d='M99.6278 21.0532V29.5905C99.6278 30.1678 99.766 30.5825 100.034 30.8345C100.303 31.0866 100.766 31.2085 101.417 31.2085H103.376V33.8673H100.856C99.4164 33.8673 98.3188 33.5339 97.5545 32.8591C96.7902 32.1842 96.408 31.0947 96.408 29.5824V21.045H94.5867V18.4432H96.408V14.6055H99.6278V18.4432H103.376V21.045H99.6278V21.0532Z'
              fill='#020044'
            />
            <Path
              d='M119.865 18.4517V33.8676H116.678V32.0463C116.174 32.6805 115.515 33.1765 114.702 33.5424C113.889 33.9083 113.027 34.0872 112.117 34.0872C110.905 34.0872 109.816 33.8351 108.856 33.331C107.897 32.8269 107.141 32.0789 106.588 31.095C106.035 30.1031 105.759 28.916 105.759 27.5094V18.4517H108.921V27.0459C108.921 28.4281 109.263 29.4851 109.954 30.225C110.645 30.9649 111.588 31.3308 112.784 31.3308C113.979 31.3308 114.922 30.9649 115.621 30.225C116.32 29.4851 116.67 28.4281 116.67 27.0459V18.4517H119.865Z'
              fill='#020044'
            />
            <Path
              d='M128.695 18.8578C129.468 18.4188 130.387 18.1992 131.452 18.1992V21.5003H130.639C129.386 21.5003 128.443 21.8174 127.801 22.4516C127.159 23.0858 126.833 24.1835 126.833 25.7527V33.8672H123.646V18.4513H126.833V20.6872C127.297 19.9067 127.915 19.2969 128.695 18.8578Z'
              fill='#020044'
            />
            <Path
              d='M147.99 27.3547H136.208C136.298 28.5824 136.761 29.5744 137.582 30.3224C138.404 31.0704 139.412 31.4444 140.607 31.4444C142.323 31.4444 143.534 30.7289 144.241 29.2898H147.681C147.217 30.7045 146.372 31.8672 145.152 32.7698C143.932 33.6723 142.412 34.1276 140.607 34.1276C139.135 34.1276 137.81 33.7942 136.647 33.1356C135.485 32.477 134.566 31.542 133.907 30.3387C133.249 29.1353 132.915 27.7449 132.915 26.1513C132.915 24.5658 133.241 23.1673 133.883 21.9639C134.525 20.7606 135.428 19.8337 136.599 19.1832C137.761 18.5328 139.103 18.2075 140.615 18.2075C142.071 18.2075 143.363 18.5246 144.502 19.1588C145.64 19.793 146.526 20.6874 147.16 21.8338C147.795 22.9803 148.112 24.2975 148.112 25.7935C148.095 26.3627 148.063 26.8831 147.99 27.3547ZM144.77 24.7772C144.754 23.5982 144.331 22.6632 143.51 21.9477C142.688 21.2403 141.672 20.8825 140.461 20.8825C139.363 20.8825 138.42 21.2322 137.631 21.9314C136.851 22.6307 136.379 23.582 136.233 24.7691H144.77V24.7772Z'
              fill='#020044'
            />
            <Path
              d='M33.0774 35.0712H16.7915C16.1654 35.0712 15.6613 34.567 15.6613 33.941V9.89011C15.6613 9.67058 15.4824 9.4917 15.2629 9.4917H13.7262C13.5066 9.4917 13.3278 9.67058 13.3278 9.89011V35.4126C13.3278 36.5103 14.214 37.3966 15.3117 37.3966H33.0774V35.0712Z'
              fill='#020044'
            />
            <Path
              d='M37.1265 35.0713V37.4048H40.8422C41.0618 37.4048 41.2406 37.226 41.2406 37.0064V35.4697C41.2406 35.2502 41.0618 35.0713 40.8422 35.0713H37.1265V35.0713Z'
              fill='#020044'
            />
            <Path
              d='M34.2808 14.4673H16.515V16.8008H32.801C33.4271 16.8008 33.9312 17.3049 33.9312 17.931V41.9819C33.9312 42.2014 34.11 42.3803 34.3296 42.3803H35.8663C36.0858 42.3803 36.2647 42.2014 36.2647 41.9819V16.4512C36.2647 15.3535 35.3784 14.4673 34.2808 14.4673Z'
              fill='#020044'
            />
            <Path
              d='M12.4659 14.4673H8.75009C8.53056 14.4673 8.35168 14.6462 8.35168 14.8657V16.4024C8.35168 16.6219 8.53056 16.8008 8.75009 16.8008H12.4659V14.4673Z'
              fill='#020044'
            />
          </Svg>
          <Text style={headerStyles.headerTitle}>Trainual Capture</Text>
          <Text style={headerStyles.headerSubtitle}>{data.title}</Text>
        </View>
        <View style={basicStyles.main}>
          <View style={heroStyles.hero}>
            <Image style={heroStyles.heroBg} src={heroBg} />
            <Text style={heroStyles.heroTitle}>{data.title}</Text>
            <View style={heroStyles.heroInfoContainer}>
              <View style={heroStyles.heroInfoItem}>
                <Svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <Path
                    d='M7.28682 1.46525C7.49178 1.36276 7.69674 1.31152 7.92732 1.31152C8.13227 1.31152 8.33723 1.36276 8.54219 1.46525L13.9223 3.74546C14.2554 3.89918 14.486 4.23225 14.486 4.59094C14.486 4.97524 14.2554 5.30831 13.9223 5.46203L8.54219 7.74225C8.33723 7.84473 8.13227 7.87035 7.92732 7.87035C7.69674 7.87035 7.49178 7.84473 7.28682 7.74225L1.90667 5.46203C1.57361 5.30831 1.36865 4.97524 1.36865 4.59094C1.36865 4.23225 1.57361 3.89918 1.90667 3.74546L7.28682 1.46525ZM7.92732 2.5413C7.85046 2.5413 7.79922 2.56692 7.7736 2.59254L3.05956 4.59094L7.7736 6.61495C7.79922 6.64057 7.85046 6.64057 7.92732 6.64057C7.97856 6.64057 8.02979 6.64057 8.05541 6.61495L12.7695 4.59094L8.05541 2.59254C8.02979 2.56692 7.97856 2.5413 7.92732 2.5413ZM7.7736 9.89436C7.79922 9.91998 7.85046 9.91998 7.92732 9.91998C7.97856 9.91998 8.02979 9.91998 8.05541 9.89436L12.9488 7.81911C12.8207 7.63976 12.7951 7.40918 12.8976 7.20422C13.0513 6.89677 13.4099 6.76867 13.7174 6.92239L13.948 7.05049C14.281 7.20422 14.486 7.51166 14.486 7.87035C14.486 8.25465 14.2554 8.58772 13.8967 8.74144L8.54219 11.0217C8.33723 11.1241 8.13227 11.1498 7.92732 11.1498C7.69674 11.1498 7.49178 11.1241 7.28682 11.0217L1.93229 8.74144C1.57361 8.58772 1.36865 8.25465 1.36865 7.84473C1.36865 7.53728 1.49675 7.25546 1.75295 7.07612L2.03477 6.87115C2.29096 6.69181 2.67526 6.74305 2.88022 7.02487C3.05956 7.25546 3.03394 7.58852 2.82898 7.79349L7.7736 9.89436ZM2.88022 10.3043C3.05956 10.5349 3.03394 10.8679 2.82898 11.0729L7.7736 13.1738C7.79922 13.1994 7.85046 13.1994 7.92732 13.1994C7.97856 13.1994 8.02979 13.1994 8.05541 13.1738L12.9488 11.0985C12.8207 10.9192 12.7951 10.6886 12.8976 10.4836C13.0513 10.1762 13.4099 10.0481 13.7174 10.2018L13.948 10.3299C14.281 10.4836 14.486 10.7911 14.486 11.1498C14.486 11.5341 14.2554 11.8671 13.8967 12.0209L8.54219 14.3011C8.33723 14.4036 8.13227 14.4292 7.92732 14.4292C7.69674 14.4292 7.49178 14.4036 7.28682 14.3011L1.93229 12.0209C1.57361 11.8671 1.36865 11.5341 1.36865 11.1241C1.36865 10.8167 1.49675 10.5349 1.75295 10.3555L2.03477 10.1506C2.29096 9.97122 2.67526 10.0225 2.88022 10.3043Z'
                    fill='#E5E5E5'
                  />
                </Svg>
                <Text style={heroStyles.heroInfoItemText}>{data.arr.length} steps</Text>
              </View>
              <View style={heroStyles.heroInfoItem}>
                <Svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <Path
                    d='M9.20248 9.08829H6.95374C4.65816 9.08829 2.83105 10.9388 2.83105 13.211C2.83105 13.6326 3.159 13.9606 3.58063 13.9606H12.5756C12.9738 13.9606 13.3252 13.6326 13.3252 13.211C13.3252 10.9388 11.4746 9.08829 9.20248 9.08829ZM3.95542 12.8362C4.14282 11.3605 5.40774 10.2127 6.95374 10.2127H9.20248C10.7251 10.2127 11.99 11.3605 12.1774 12.8362H3.95542ZM8.07811 7.96392C9.71782 7.96392 11.0764 6.62874 11.0764 4.96561C11.0764 3.3259 9.71782 1.96729 8.07811 1.96729C6.41498 1.96729 5.07979 3.3259 5.07979 4.96561C5.07979 6.62874 6.41498 7.96392 8.07811 7.96392ZM8.07811 3.09166C9.10879 3.09166 9.95206 3.93493 9.95206 4.96561C9.95206 6.0197 9.10879 6.83956 8.07811 6.83956C7.02402 6.83956 6.20416 6.0197 6.20416 4.96561C6.20416 3.93493 7.02402 3.09166 8.07811 3.09166Z'
                    fill='#E5E5E5'
                  />
                </Svg>
                <Text style={heroStyles.heroInfoItemText}>Created by {data.author}</Text>
              </View>
            </View>
            <View style={heroStyles.heroButtonsContainer}>
              {data.arr.length &&
                data.arr.map((step, index) => {
                  return (
                    detectURLs(step.name) &&
                    detectURLs(step.name).map((url, index) => (
                      <View style={heroStyles.heroButtonWrapper} key={index}>
                        <View style={buttonStyles.buttonContainer}>
                          <Svg
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <Path
                              d='M17.3531 5.10441C17.6835 5.91734 17.7667 6.80967 17.5924 7.66969C17.4181 8.52971 16.9939 9.31919 16.3731 9.93931L15.3819 10.9306C15.2702 11.0395 15.1201 11.0999 14.9641 11.0989C14.8081 11.0979 14.6588 11.0355 14.5485 10.9252C14.4383 10.8149 14.3759 10.6656 14.3748 10.5097C14.3738 10.3537 14.4343 10.2036 14.5432 10.0919L15.5344 9.10071C16.1436 8.49152 16.4884 7.66694 16.4942 6.80542C16.5 5.9439 16.1663 5.11475 15.5654 4.49741C15.2592 4.18195 14.8916 3.93256 14.4854 3.76461C13.8808 3.51548 13.2161 3.45083 12.5749 3.57882C11.9337 3.7068 11.3447 4.02169 10.8822 4.48381L9.90876 5.45721C9.79706 5.56606 9.64697 5.62653 9.491 5.62553C9.33503 5.62452 9.18574 5.56211 9.07545 5.45182C8.96516 5.34153 8.90275 5.19224 8.90175 5.03627C8.90074 4.8803 8.96121 4.73021 9.07006 4.61851L10.0614 3.62731C10.5824 3.10665 11.2243 2.723 11.9296 2.51054C12.6349 2.29809 13.3818 2.26344 14.1038 2.40967C14.8258 2.55591 15.5003 2.87849 16.0674 3.34867C16.6344 3.81885 17.0764 4.42201 17.3538 5.10441H17.3531Z'
                              fill='#555555'
                            />
                            <Path
                              d='M12.9839 7.01615C13.1021 7.13463 13.1684 7.29515 13.1684 7.4625C13.1684 7.62985 13.1021 7.79037 12.9839 7.90885L7.90876 12.984C7.85015 13.0426 7.78056 13.0891 7.70397 13.1208C7.62739 13.1525 7.54531 13.1688 7.46241 13.1688C7.37952 13.1688 7.29743 13.1525 7.22085 13.1208C7.14426 13.0891 7.07468 13.0426 7.01606 12.984C6.95745 12.9253 6.91095 12.8557 6.87923 12.7792C6.8475 12.7026 6.83118 12.6205 6.83118 12.5376C6.83118 12.4547 6.8475 12.3726 6.87923 12.296C6.91095 12.2195 6.95745 12.1499 7.01606 12.0913L12.0912 7.01615C12.2096 6.89794 12.3702 6.83154 12.5375 6.83154C12.7049 6.83154 12.8654 6.89794 12.9839 7.01615Z'
                              fill='#555555'
                            />
                            <Path
                              d='M10.9306 14.5426C11.0416 14.6539 11.104 14.8047 11.104 14.962C11.104 15.1192 11.0416 15.27 10.9306 15.3813L9.93927 16.3725C9.52546 16.7899 9.0333 17.1215 8.49101 17.3482C7.94873 17.5749 7.36701 17.6922 6.77926 17.6935C6.1915 17.6948 5.60928 17.5799 5.06602 17.3556C4.52276 17.1313 4.02915 16.8018 3.61354 16.3862C3.19794 15.9706 2.86851 15.477 2.64417 14.9338C2.41984 14.3905 2.30501 13.8083 2.30628 13.2205C2.30756 12.6328 2.42491 12.051 2.6516 11.5088C2.87829 10.9665 3.20986 10.4743 3.62727 10.0605L4.61847 9.06921C4.73017 8.96035 4.88026 8.89988 5.03623 8.90089C5.1922 8.9019 5.34149 8.96431 5.45178 9.07459C5.56207 9.18488 5.62447 9.33418 5.62548 9.49015C5.62649 9.64611 5.56602 9.7962 5.45717 9.90791L4.48377 10.8813C4.02215 11.3442 3.7076 11.9331 3.57964 12.5742C3.45169 13.2153 3.51602 13.8799 3.76457 14.4845C3.93252 14.8908 4.18191 15.2583 4.49737 15.5645C5.10797 16.1617 5.92866 16.4953 6.78277 16.4934H6.78377C7.21423 16.4945 7.64065 16.4103 8.03833 16.2455C8.43601 16.0807 8.79706 15.8387 9.10057 15.5334L10.0919 14.5422C10.2032 14.4312 10.3541 14.3689 10.5113 14.369C10.6685 14.3691 10.8193 14.4315 10.9306 14.5426Z'
                              fill='#555555'
                            />
                          </Svg>
                          <Link style={buttonStyles.buttonLink} key={index} src={url}>
                            {getUrl(url)}
                          </Link>
                        </View>
                      </View>
                    ))
                  );
                })}
            </View>
          </View>
          {data.arr.length &&
            data.arr.map((step, index) => (
              <View style={stepStyles.step} wrap={false} key={index}>
                <View>
                  <View style={stepStyles.stepHead}>
                    <Text style={stepStyles.stepNumber}>{index + 1}</Text>
                    <View>
                      <View style={stepStyles.stepTitleInner}>
                        <Text style={stepStyles.stepTitle}>
                          {deleteURLs(step.name.replace(/(\r\n|\n|\r)/gm, ''))}
                        </Text>
                        {detectURLs(step.name) &&
                          detectURLs(step.name).map((url, index) => (
                            <View style={buttonStyles.buttonContainer} key={index}>
                              <Svg
                                width='20'
                                height='20'
                                viewBox='0 0 20 20'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <Path
                                  d='M17.3531 5.10441C17.6835 5.91734 17.7667 6.80967 17.5924 7.66969C17.4181 8.52971 16.9939 9.31919 16.3731 9.93931L15.3819 10.9306C15.2702 11.0395 15.1201 11.0999 14.9641 11.0989C14.8081 11.0979 14.6588 11.0355 14.5485 10.9252C14.4383 10.8149 14.3759 10.6656 14.3748 10.5097C14.3738 10.3537 14.4343 10.2036 14.5432 10.0919L15.5344 9.10071C16.1436 8.49152 16.4884 7.66694 16.4942 6.80542C16.5 5.9439 16.1663 5.11475 15.5654 4.49741C15.2592 4.18195 14.8916 3.93256 14.4854 3.76461C13.8808 3.51548 13.2161 3.45083 12.5749 3.57882C11.9337 3.7068 11.3447 4.02169 10.8822 4.48381L9.90876 5.45721C9.79706 5.56606 9.64697 5.62653 9.491 5.62553C9.33503 5.62452 9.18574 5.56211 9.07545 5.45182C8.96516 5.34153 8.90275 5.19224 8.90175 5.03627C8.90074 4.8803 8.96121 4.73021 9.07006 4.61851L10.0614 3.62731C10.5824 3.10665 11.2243 2.723 11.9296 2.51054C12.6349 2.29809 13.3818 2.26344 14.1038 2.40967C14.8258 2.55591 15.5003 2.87849 16.0674 3.34867C16.6344 3.81885 17.0764 4.42201 17.3538 5.10441H17.3531Z'
                                  fill='#555555'
                                />
                                <Path
                                  d='M12.9839 7.01615C13.1021 7.13463 13.1684 7.29515 13.1684 7.4625C13.1684 7.62985 13.1021 7.79037 12.9839 7.90885L7.90876 12.984C7.85015 13.0426 7.78056 13.0891 7.70397 13.1208C7.62739 13.1525 7.54531 13.1688 7.46241 13.1688C7.37952 13.1688 7.29743 13.1525 7.22085 13.1208C7.14426 13.0891 7.07468 13.0426 7.01606 12.984C6.95745 12.9253 6.91095 12.8557 6.87923 12.7792C6.8475 12.7026 6.83118 12.6205 6.83118 12.5376C6.83118 12.4547 6.8475 12.3726 6.87923 12.296C6.91095 12.2195 6.95745 12.1499 7.01606 12.0913L12.0912 7.01615C12.2096 6.89794 12.3702 6.83154 12.5375 6.83154C12.7049 6.83154 12.8654 6.89794 12.9839 7.01615Z'
                                  fill='#555555'
                                />
                                <Path
                                  d='M10.9306 14.5426C11.0416 14.6539 11.104 14.8047 11.104 14.962C11.104 15.1192 11.0416 15.27 10.9306 15.3813L9.93927 16.3725C9.52546 16.7899 9.0333 17.1215 8.49101 17.3482C7.94873 17.5749 7.36701 17.6922 6.77926 17.6935C6.1915 17.6948 5.60928 17.5799 5.06602 17.3556C4.52276 17.1313 4.02915 16.8018 3.61354 16.3862C3.19794 15.9706 2.86851 15.477 2.64417 14.9338C2.41984 14.3905 2.30501 13.8083 2.30628 13.2205C2.30756 12.6328 2.42491 12.051 2.6516 11.5088C2.87829 10.9665 3.20986 10.4743 3.62727 10.0605L4.61847 9.06921C4.73017 8.96035 4.88026 8.89988 5.03623 8.90089C5.1922 8.9019 5.34149 8.96431 5.45178 9.07459C5.56207 9.18488 5.62447 9.33418 5.62548 9.49015C5.62649 9.64611 5.56602 9.7962 5.45717 9.90791L4.48377 10.8813C4.02215 11.3442 3.7076 11.9331 3.57964 12.5742C3.45169 13.2153 3.51602 13.8799 3.76457 14.4845C3.93252 14.8908 4.18191 15.2583 4.49737 15.5645C5.10797 16.1617 5.92866 16.4953 6.78277 16.4934H6.78377C7.21423 16.4945 7.64065 16.4103 8.03833 16.2455C8.43601 16.0807 8.79706 15.8387 9.10057 15.5334L10.0919 14.5422C10.2032 14.4312 10.3541 14.3689 10.5113 14.369C10.6685 14.3691 10.8193 14.4315 10.9306 14.5426Z'
                                  fill='#555555'
                                />
                              </Svg>
                              <Link style={buttonStyles.buttonLink} key={index} src={url}>
                                {getUrl(url)}
                              </Link>
                            </View>
                          ))}
                      </View>
                      {step.desc && <Text style={stepStyles.stepDesc}>{step.desc}</Text>}
                    </View>
                  </View>
                </View>
                {step.imageURL && <Image style={stepStyles.stepImage} src={step.imageURL} />}
              </View>
            ))}
        </View>

        <Text
          style={basicStyles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PDFFile;
